/**
 * Sheet 50, Settings › Billing: plan and credits (mission 9 WP13 T078; FR-014, FR-019, FR-023, NFR-011,
 * NFR-012; C-002, C-007, C-008, C-012; B.1, B.4, B.5, B.7, T.6; OR-13, OR-14; squad S22, U24, U28, U29;
 * contracts/ui-api.md "Sheet 50").
 *
 * Inside `SettingsLayout` (50/1), within the shell, which owns the one `main` and whose billing banner is
 * suppressed on this route: `DunningBand` owns the message here (50/10). On open the page makes one
 * billing read (`billingQuery`) and one ledger page (`UsageLedger`); the balance is the chip's own
 * `['credits']` read, so nothing else is fetched (NFR-012). The sections follow the drawn order: the
 * plan card (50/2, 50/3), the credits panel with the soft-cap status and the purchased blocks (50/4 to
 * 50/6), the one `MeteringStatement` (50/7; OR-13), buy credits (50/8), the card on file (50/9), the usage
 * ledger (50/11, 50/12), and the invoices and statements (50/13). The dunning band leads, as a page band.
 *
 * A hash-scroll effect brings `#card` into view on `/app/settings/billing#card`, once the card is on the
 * page (U28): every "Update card" remedy lands there. The unreachable `pending` state is dropped (U24):
 * the gate sends a pending tenant to `/app/subscribe`. One identity per tenant, so no Auditor view and no
 * "Ask an Owner" (C-007).
 */
import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

import { copyText } from '@/lib/provisional-copy'
import { billingQuery } from '@/lib/queries'
import { MeteringStatement } from '../../subscribe/MeteringStatement'
import { SettingsLayout } from '../SettingsLayout'
import { BillingDocuments } from './BillingDocuments'
import { BillingProblem } from './BillingProblem'
import { BlocksTable } from './BlocksTable'
import { BuyCredits } from './BuyCredits'
import { CardOnFile } from './CardOnFile'
import { CreditsPanel } from './CreditsPanel'
import { DunningBand } from './DunningBand'
import { PlanCard } from './PlanCard'
import { SoftCapStatus } from './SoftCapStatus'
import { UsageLedger } from './UsageLedger'

export default function Billing() {
  const billing = useQuery(billingQuery())
  const { hash } = useLocation()
  const view = billing.data
  const ready = view !== undefined

  // U28: the card is on the page only once the read has landed.
  useEffect(() => {
    if (ready && hash === '#card') document.getElementById('card')?.scrollIntoView()
  }, [ready, hash])

  let body
  if (view !== undefined) {
    body = (
      <>
        <DunningBand access={view.access} />
        <PlanCard view={view} />
        <CreditsPanel view={view}>
          <SoftCapStatus softCap={view.soft_cap} />
          <BlocksTable blocks={view.blocks} />
        </CreditsPanel>
        <MeteringStatement />
        <BuyCredits view={view} />
        <CardOnFile view={view} />
        <UsageLedger />
        <BillingDocuments view={view} />
      </>
    )
  } else if (billing.isError) {
    body = <BillingProblem error={billing.error} />
  } else {
    body = <p className="text-body-sm text-navy-600">{copyText('loading')}</p>
  }

  return (
    <SettingsLayout>
      <h1 className="text-h1 text-navy-950">{copyText('billingTitle')}</h1>
      {body}
    </SettingsLayout>
  )
}
